import React from "react";
import { motion } from "framer-motion";
import {
  alpha,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

// ICONS
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";

const itemVariants = {
  hidden: { y: 10, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

export default function DriverListRow({
  index,
  driver,
  deleteAction,
  editAction,
}) {
  const theme = useTheme();

  return (
    <motion.div
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      layout
    >
      <Paper
        elevation={0}
        component={motion.div}
        whileHover={{
          y: -2,
          boxShadow: theme.shadows[2],
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
        }}
        sx={{
          mb: 2,
          borderRadius: 2,
          backgroundColor: theme.palette.grey[50],
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
          transition: "all 0.3s ease",
          "&:hover": {
            cursor: "pointer",
            borderColor: theme.palette.grey[300],
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            {/* Left side - Driver info */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ flex: 1 }}
            >
              {/* Index Badge */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  borderRadius: 1.5,
                  p: 0.75,
                  minWidth: 36,
                  height: 36,
                }}
              >
                <PersonIcon fontSize="small" />
              </Box>

              <Stack direction="column" spacing={0.5}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {driver.name}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {driver.phone && (
                    <Chip
                      icon={<LocalPhoneIcon sx={{ fontSize: 14 }} />}
                      label={driver.phone}
                      size="small"
                      sx={{
                        height: 24,
                        bgcolor: alpha(theme.palette.grey[500], 0.1),
                        color: theme.palette.text.secondary,
                        "& .MuiChip-icon": {
                          color: theme.palette.text.secondary,
                        },
                      }}
                    />
                  )}
                  {driver.plate && (
                    <Chip
                      icon={<DirectionsCarIcon sx={{ fontSize: 14 }} />}
                      label={driver.plate}
                      size="small"
                      sx={{
                        height: 24,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main,
                        "& .MuiChip-icon": {
                          color: theme.palette.info.main,
                        },
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack>

            {/* Right side - Actions */}
            <Stack direction="row" spacing={1}>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Tooltip title="Editar entregador">
                  <IconButton
                    size="small"
                    onClick={editAction}
                    sx={{
                      color: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      p: 0.75,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Tooltip title="Remover entregador">
                  <IconButton
                    size="small"
                    onClick={deleteAction}
                    sx={{
                      color: theme.palette.error.main,
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      p: 0.75,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.error.main, 0.2),
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </motion.div>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </motion.div>
  );
}
